import { useMutation, useQueryClient } from '@tanstack/react-query';
import { showToast } from '@tix-ticketing/ui-components';
import type { AxiosError } from 'axios';
import { useCallback, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { getApiProfileOrdersQueryKey, postApiOrdersByEncryptedOrderIdSplitMutation } from 'api/generated/@tanstack/react-query.gen';
import { deleteApiOrdersByEncryptedOrderId } from 'api/generated/sdk.gen';
import { ReleaseItemsDialog } from 'components/common/dialogs/release-items-dialog';
import { useAppState } from 'lib/app-state';
import { toastError } from 'lib/helpers/error-toast-helpers';
import { getAllOrderTickets } from 'lib/helpers/order-helpers';
import { buildSelectedOrder, getAllItemKeys, giftCardKey, membershipKey, ticketKey } from 'lib/helpers/pay-selection-helpers';
import { findSubscriptionLimitViolation } from 'lib/helpers/subscription-limit-helpers';
import { useGoToCheckout } from 'lib/hooks/use-checkout-navigation';
import { useSubscriptionLimits } from 'lib/hooks/use-subscription-limits';

/**
 * The order as the cart shows it: a reservation opened from My pages minus
 * whatever the buyer has removed but not yet committed. Split out from
 * `useReservationRemovals` for the places that only have to count or price what
 * is left, such as the cart badge in the navbar.
 */
export function useReservationKeptOrder() {
  const {
    eventData,
    isReservationOrder,
    isRenewalOrder,
    removedOrderItemKeys
  } = useAppState();
  const order = eventData.order;

  // A reservation is not an in-progress basket, so the backend turns its
  // `canEdit`/`canRemoveItems` flags off — but nothing here edits or deletes
  // it. Items are only hidden from the cart until checkout settles them in one
  // split, so what app state says about the order opened from My pages is the
  // whole gate. A renewal is the same kind of order — held by the venue rather
  // than built here — and settles the same way.
  const isActive = (isReservationOrder || isRenewalOrder) && !!order?.encryptedId;
  const removed = useMemo(() => new Set(isActive ? removedOrderItemKeys : []), [isActive, removedOrderItemKeys]);
  const allKeys = useMemo(() => getAllItemKeys(order), [order]);
  const keptKeys = useMemo(() => new Set(allKeys.filter(key => !removed.has(key))), [allKeys, removed]);

  // Keys are only counted while they still match something on the order, so a
  // removal the backend has since undone cannot keep the dialog coming up.
  const hasRemovals = keptKeys.size < allKeys.length;
  return {
    isActive,
    removed,
    allKeys,
    hasRemovals,
    hasKeptItems: keptKeys.size > 0,
    keptOrder: useMemo(() => hasRemovals ? buildSelectedOrder(order, keptKeys) : order, [hasRemovals, order, keptKeys])
  };
}

/**
 * Removing an item from a reservation opened in the cart is a soft delete: the
 * item disappears from the cart and the summary reprices without it, but the
 * reservation goes on holding it until the buyer checks out. On checkout the
 * removed items are moved onto a new reservation by
 * `POST /api/orders/{id}/split`, which the buyer can either keep or, having
 * asked to release them, have deleted right after.
 *
 * Only offered while the cart holds an order the venue is holding for the buyer
 * — a reservation or a subscription renewal opened from My pages — which app
 * state tells apart from a buyer's own freshly-built basket, where a removal
 * deletes for real.
 *
 * Render `releaseItemsDialog` alongside the cart and route the cart's checkout
 * action through `proceedToCheckout`.
 */
export function useReservationRemovals() {
  const intl = useIntl();
  const queryClient = useQueryClient();
  const {
    eventData,
    setEventData,
    isRenewalOrder,
    setRemovedOrderItemKeys
  } = useAppState();
  const {
    goToCheckout,
    isChecking
  } = useGoToCheckout();
  const order = eventData.order;
  const {
    isActive,
    removed,
    allKeys,
    hasRemovals,
    hasKeptItems,
    keptOrder
  } = useReservationKeptOrder();
  const subscriptionLimits = useSubscriptionLimits(isActive ? order : undefined);
  const [isPrompting, setIsPrompting] = useState(false);
  // Where the caller was heading when it asked. The mini cart keeps its own destination and closes
  // its drawer on the way, so losing it here leaves the buyer on an event page with no cart in
  // reach and a toast telling them to go and look at it.
  const navigateAfterCommit = useRef<(() => void) | undefined>(undefined);
  // The drawer form of the dialog stays mounted once rendered, and the cart
  // page and the mini-cart each hold their own copy of this hook. Only the one
  // that has actually asked is put on the page.
  const [hasPrompted, setHasPrompted] = useState(false);
  const [pendingAction, setPendingAction] = useState<'save' | 'release' | null>(null);
  const isRemoved = useCallback((key: string) => removed.has(key), [removed]);

  /**
   * Removing the last item is not a removal but a change of mind about the
   * whole reservation: nothing would be left to check out and nothing for the
   * split to keep. So the cart lets go of it instead — no request is made, the
   * reservation goes on holding everything and shows up under Reservations
   * again, and the flow hands the buyer back to My pages now that the order
   * has left storage.
   */
  const detach = useCallback(() => {
    setEventData(current => ({
      ...current,
      order: undefined
    }));
    setRemovedOrderItemKeys([]);
  }, [setEventData, setRemovedOrderItemKeys]);
  const remove = useCallback((keys: string[]) => {
    if (keys.length === 0) return;
    const next = new Set([...removed, ...keys]);
    // Donations do not count: the split cannot move one, so the cart deletes
    // a donation outright and it cannot be what holds the cart open. A
    // membership is deleted outright too, unless it is the last item left.
    const emptiesTheOrder = allKeys.length > 0 && allKeys.every(key => next.has(key));
    if (isActive && emptiesTheOrder) {
      detach();
      return;
    }

    // A subscription is sold as a number of seats over a number of events and
    // has to go on satisfying both, so a removal that would leave one short is
    // turned down here rather than at checkout.
    const violation = findSubscriptionLimitViolation(order, next, subscriptionLimits);
    if (violation) {
      showToast({
        title: violation.kind === 'seats' ? intl.formatMessage({
          id: "cart.subscriptionLimit.seatsTitle",
          defaultMessage: [{
            "type": 0,
            "value": "Not enough seats"
          }]
        }) : intl.formatMessage({
          id: "cart.subscriptionLimit.eventsTitle",
          defaultMessage: [{
            "type": 0,
            "value": "Not enough events"
          }]
        }),
        message: violation.kind === 'seats' ? intl.formatMessage({
          id: "cart.subscriptionLimit.seatsMessage",
          defaultMessage: [{
            "type": 0,
            "value": "This subscription has to keep at least "
          }, {
            "type": 6,
            "value": "min",
            "options": {
              "one": {
                "value": [{
                  "type": 7
                }, {
                  "type": 0,
                  "value": " seat"
                }]
              },
              "other": {
                "value": [{
                  "type": 7
                }, {
                  "type": 0,
                  "value": " seats"
                }]
              }
            },
            "offset": 0,
            "pluralType": "cardinal"
          }, {
            "type": 0,
            "value": " for every event it covers."
          }]
        }, {
          min: violation.min
        }) : intl.formatMessage({
          id: "cart.subscriptionLimit.eventsMessage",
          defaultMessage: [{
            "type": 0,
            "value": "This subscription has to cover at least "
          }, {
            "type": 6,
            "value": "min",
            "options": {
              "one": {
                "value": [{
                  "type": 7
                }, {
                  "type": 0,
                  "value": " event"
                }]
              },
              "other": {
                "value": [{
                  "type": 7
                }, {
                  "type": 0,
                  "value": " events"
                }]
              }
            },
            "offset": 0,
            "pluralType": "cardinal"
          }, {
            "type": 0,
            "value": "."
          }]
        }, {
          min: violation.min
        }),
        severity: 'warning'
      });
      return;
    }
    setRemovedOrderItemKeys(current => [...current, ...keys.filter(key => !current.includes(key))]);
  }, [allKeys, detach, intl, isActive, order, removed, setRemovedOrderItemKeys, subscriptionLimits]);
  const splitMutation = useMutation({
    ...postApiOrdersByEncryptedOrderIdSplitMutation(),
    // The same rules are enforced server-side and come back as a 409 carrying
    // the reason, which catches whatever the cart could not know about. A
    // refused split changed nothing, so the removals are dropped and the cart
    // shows the order as it really is. Set here rather than on `mutate` so it
    // replaces the client-wide generic toast instead of adding a second one.
    onError: error => {
      setIsPrompting(false);
      setPendingAction(null);
      setRemovedOrderItemKeys([]);
      toastError(error.response?.data as Parameters<typeof toastError>[0]);
    }
  });
  const commit = (release: boolean) => {
    if (!order?.encryptedId) return;
    const removedOf = <T,>(items: T[] | null | undefined, key: (item: T) => string) => (items ?? []).filter(item => removed.has(key(item)));
    setPendingAction(release ? 'release' : 'save');
    splitMutation.mutate({
      path: {
        encryptedOrderId: order.encryptedId
      },
      body: {
        encryptedTicketIds: removedOf(getAllOrderTickets(order), ticketKey).map(ticket => ticket.encryptedId ?? ''),
        encryptedGiftCardIds: removedOf(order.giftCards, giftCardKey).map(giftCard => giftCard.encryptedGiftCardId ?? ''),
        encryptedOrderMembershipIds: removedOf(order.memberships, membershipKey).map(membership => membership.encryptedOrderMembershipId ?? ''),
        // Order products do not exist in the new flow, and the endpoint
        // rejects a non-empty list rather than silently dropping it.
        encryptedOrderProductIds: []
      }
    }, {
      onSuccess: async response => {
        if (response.order) {
          setEventData(current => ({
            ...current,
            order: response.order
          }));
        }
        if (release && response.splitOffOrderEncryptedId) {
          try {
            await deleteApiOrdersByEncryptedOrderId({
              path: {
                encryptedOrderId: response.splitOffOrderEncryptedId
              },
              throwOnError: true
            });
          } catch (error) {
            // The split has already happened, so a failure here leaves the
            // items on a reservation of their own rather than losing them —
            // say so and carry on to checkout.
            toastError((error as AxiosError).response?.data as Parameters<typeof toastError>[0]);
          }
        }
        // The items left behind are a new reservation on My pages, and the
        // order being paid is no longer the one that was listed there.
        queryClient.invalidateQueries({
          queryKey: getApiProfileOrdersQueryKey()
        });
        setRemovedOrderItemKeys([]);
        setIsPrompting(false);
        setPendingAction(null);
        const navigate = navigateAfterCommit.current;
        navigateAfterCommit.current = undefined;
        if (navigate) navigate();else goToCheckout(response.order?.encryptedId);
      }
    });
  };

  /**
   * Stands in for the cart's checkout navigation: with something removed it
   * asks what to do with it first, and otherwise carries on. `navigate` lets a
   * caller keep its own destination.
   */
  const proceedToCheckout = (navigate?: () => void) => {
    if (isActive && hasRemovals) {
      navigateAfterCommit.current = navigate;
      setHasPrompted(true);
      setIsPrompting(true);
      return;
    }
    (navigate ?? goToCheckout)();
  };
  return {
    isActive,
    hasRemovals,
    hasKeptItems,
    isRemoved,
    remove,
    keptOrder,
    proceedToCheckout,
    isCommitting: splitMutation.isPending,
    isCheckingOut: isChecking,
    releaseItemsDialog: hasPrompted ? <ReleaseItemsDialog open={isPrompting} variant={isRenewalOrder ? 'subscription' : 'reservation'} pendingAction={pendingAction} onClose={() => setIsPrompting(false)} onSaveAsNewReservation={() => commit(false)} onRelease={() => commit(true)} /> : null
  };
}
export type ReservationRemovals = ReturnType<typeof useReservationRemovals>;